import { useCallback, useEffect, useState } from 'react';
import { Plus, ShieldCheck, User as UserIcon, Trash2, AlertTriangle } from 'lucide-react';
import { api } from '../lib/api';

type Nutzer = { id: number; username: string; email: string; role: string; is_active: boolean };

/**
 * Benutzerverwaltung.
 *
 * Anlegen geht ueber POST /admin/users, Aendern ueber **PUT**
 * /admin/users/{id} — nicht PATCH; das Backend kennt nur PUT, und der
 * Rumpf akzeptiert genau `role` und `is_active`.
 *
 * Passwoerter werden nirgends angezeigt und nirgends zurueckgelesen: Das
 * Backend gibt sie nicht heraus, und diese Seite fragt sie nur beim
 * Anlegen einmal ab.
 */
export function Users() {
  const [nutzer, setNutzer] = useState<Nutzer[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [neu, setNeu] = useState(false);
  const [deaktivieren, setDeaktivieren] = useState<Nutzer | null>(null);

  const laden = useCallback(async () => {
    try {
      setNutzer(await api<Nutzer[]>('/admin/users'));
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => { void laden(); }, [laden]);

  async function aendern(u: Nutzer, feld: Partial<Pick<Nutzer, 'role' | 'is_active'>>) {
    try {
      await api(`/admin/users/${u.id}`, { method: 'PUT', body: JSON.stringify(feld) });
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
    await laden();
  }

  if (fehler && !nutzer) return <div className="hinweis hinweis--schlecht">{fehler}</div>;
  if (!nutzer) return <div className="laedt">Lade …</div>;

  return (
    <>
      <div className="kopf">
        <div>
          <h1>Benutzer</h1>
          <p>
            {nutzer.length} {nutzer.length === 1 ? 'Konto' : 'Konten'} ·{' '}
            {nutzer.filter((u) => u.role === 'admin').length} mit Verwaltungsrechten.
            Aufträge und Downloads gehören jeweils dem Konto, das sie angelegt hat.
          </p>
        </div>
        <button className="knopf knopf--haupt" onClick={() => setNeu(true)}>
          <Plus size={16} /> Neues Konto
        </button>
      </div>

      {fehler && <div className="hinweis hinweis--schlecht" style={{ marginBottom: 14 }}>{fehler}</div>}

      <div className="karte">
        <table className="tabelle">
          <thead>
            <tr>
              <th>Benutzer</th>
              <th>E-Mail</th>
              <th style={{ width: 170 }}>Rolle</th>
              <th style={{ width: 130 }}>Aktiv</th>
              <th style={{ width: 60 }} />
            </tr>
          </thead>
          <tbody>
            {nutzer.map((u) => (
              <tr key={u.id}>
                <td>
                  <span className="reihe" style={{ gap: 8 }}>
                    {u.role === 'admin' ? <ShieldCheck size={15} className="leise" /> : <UserIcon size={15} className="schwach" />}
                    <strong>{u.username}</strong>
                  </span>
                </td>
                <td className="klein leise">{u.email}</td>
                <td>
                  <select className="feld" style={{ padding: '5px 8px' }} value={u.role}
                    onChange={(e) => aendern(u, { role: e.target.value })}>
                    <option value="user">Benutzer</option>
                    <option value="admin">Verwaltung</option>
                  </select>
                </td>
                <td>
                  <span className="reihe" style={{ gap: 9 }}>
                    <button className="schalter" data-an={u.is_active}
                      onClick={() => aendern(u, { is_active: !u.is_active })}
                      aria-label={u.is_active ? 'Deaktivieren' : 'Aktivieren'}><i /></button>
                    <span className="klein leise">{u.is_active ? 'aktiv' : 'gesperrt'}</span>
                  </span>
                </td>
                <td>
                  {u.is_active && (
                    <button className="knopf knopf--klein knopf--nur-icon knopf--gefahr"
                      title="Zugang sperren" onClick={() => setDeaktivieren(u)}>
                      <Trash2 size={15} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="klein schwach" style={{ marginTop: 12 }}>
        Konten lassen sich sperren, aber nicht löschen — sonst verlören ihre Aufträge
        und die Download-Historie ihren Bezug.
      </p>

      {neu && <NeuesKonto aufSchliessen={() => setNeu(false)} aufFertig={async () => { setNeu(false); await laden(); }} />}

      {deaktivieren && (
        <div className="overlay" onClick={() => setDeaktivieren(null)}>
          <div className="karte dialog" style={{ maxWidth: 430 }} onClick={(e) => e.stopPropagation()}>
            <div className="karte__kopf">
              <span className="karte__titel">
                <AlertTriangle size={16} style={{ verticalAlign: -3, marginRight: 6 }} />Zugang sperren?
              </span>
            </div>
            <div className="dialog__leib">
              <p style={{ marginTop: 0 }}><strong>{deaktivieren.username}</strong> kann sich danach nicht mehr anmelden.</p>
              <p className="leise klein" style={{ marginBottom: 0 }}>
                Bestehende Aufträge laufen weiter — sie gehören dem Konto, nicht der Sitzung.
                Zum Anhalten die Aufträge selbst pausieren.
              </p>
            </div>
            <div className="dialog__fuss">
              <button className="knopf" onClick={() => setDeaktivieren(null)}>Abbrechen</button>
              <button className="knopf knopf--gefahr" onClick={async () => {
                await aendern(deaktivieren, { is_active: false });
                setDeaktivieren(null);
              }}>Sperren</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function NeuesKonto({ aufSchliessen, aufFertig }: { aufSchliessen: () => void; aufFertig: () => void | Promise<void> }) {
  const [f, setF] = useState({ username: '', email: '', password: '', role: 'user' });
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  async function anlegen() {
    if (!f.username.trim() || !f.email.trim() || f.password.length < 8) {
      setFehler('Benutzername, E-Mail und ein Passwort mit mindestens 8 Zeichen sind nötig.');
      return;
    }
    setLaeuft(true);
    setFehler(null);
    try {
      await api('/admin/users', { method: 'POST', body: JSON.stringify(f) });
      await aufFertig();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="overlay" onClick={aufSchliessen}>
      <div className="karte dialog" style={{ maxWidth: 470 }} onClick={(e) => e.stopPropagation()}>
        <div className="karte__kopf"><span className="karte__titel">Neues Konto</span></div>
        <div className="dialog__leib stapel">
          {fehler && <div className="hinweis hinweis--schlecht">{fehler}</div>}
          <div>
            <label className="label" htmlFor="u-name">Benutzername</label>
            <input id="u-name" className="feld" value={f.username} autoComplete="off"
              onChange={(e) => setF({ ...f, username: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="u-mail">E-Mail</label>
            <input id="u-mail" className="feld" type="email" value={f.email} autoComplete="off"
              onChange={(e) => setF({ ...f, email: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="u-pw">Passwort</label>
            <input id="u-pw" className="feld" type="password" value={f.password} autoComplete="new-password"
              onChange={(e) => setF({ ...f, password: e.target.value })} />
            <div className="klein schwach" style={{ marginTop: 5 }}>
              Mindestens 8 Zeichen. Wird nur beim Anlegen gesetzt und danach nie wieder angezeigt.
            </div>
          </div>
          <div>
            <label className="label" htmlFor="u-rolle">Rolle</label>
            <select id="u-rolle" className="feld" value={f.role}
              onChange={(e) => setF({ ...f, role: e.target.value })}>
              <option value="user">Benutzer — eigene Aufträge und Downloads</option>
              <option value="admin">Verwaltung — zusätzlich Konten und Einstellungen</option>
            </select>
          </div>
        </div>
        <div className="dialog__fuss">
          <button className="knopf" onClick={aufSchliessen} disabled={laeuft}>Abbrechen</button>
          <button className="knopf knopf--haupt" onClick={anlegen} disabled={laeuft}>
            {laeuft ? 'Legt an …' : 'Anlegen'}
          </button>
        </div>
      </div>
    </div>
  );
}
